/** @jsx jsx */
import type { FC } from 'react';
import React, { memo, useCallback } from 'react';
import { styled, css, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import AkSpinner from '@atlaskit/spinner/spinner';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import NodeIcon from '@atlaskit/icon/core/node';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';

import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';

import type { ContentTreeItem } from './data-transformers';

const SPINNER_SIZE_MEDIUM = 16;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageTreeItemSpinner = styled.div({
	margin: `0 ${token('space.050')}`,
});

export type BeforeIconProps = {
	item: ContentTreeItem;
	isSuperAdmin?: boolean;
	onExpand(e: React.MouseEvent): void;
	onCollapse(e: React.MouseEvent): void;
};

const beforeIconStyles = css({
	zIndex: 1,
	display: 'flex',
	border: 'none',
	margin: 0,
	padding: 0,
	textDecoration: 'none',
	backgroundColor: 'transparent',
	color: token('color.icon'),
	cursor: 'pointer',
});

const beforeIconStylesNav4Styles = css({
	height: token('space.300'),
	width: token('space.300'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	color: token('color.text.subtle'),
});

export const BeforeIcon: FC<BeforeIconProps> = memo(
	({ item, isSuperAdmin, onExpand, onCollapse }) => {
		const isNav4Enabled = useIsNav4Enabled();
		const {
			isChildrenLoading,
			hasChildren,
			isExpanded,
			hasRestrictions,
			hasInheritedRestrictions,
		} = item;

		const shouldDisplayRed = Boolean(
			!!isSuperAdmin && (hasRestrictions || hasInheritedRestrictions),
		);

		const handleOnExpand = useCallback(
			(event: React.MouseEvent) => {
				traceUFOPress('page-tree-item-expand', event.timeStamp);
				onExpand(event);
			},
			[onExpand],
		);

		const handleOnCollapse = useCallback(
			(event: React.MouseEvent) => {
				traceUFOPress('page-tree-item-collapse', event.timeStamp);
				onCollapse(event);
			},
			[onCollapse],
		);

		const iconColor = shouldDisplayRed ? token('color.icon.danger') : undefined;

		if (isChildrenLoading) {
			return (
				<PageTreeItemSpinner data-testid="tree-item-spinner">
					<AkSpinner size={SPINNER_SIZE_MEDIUM} />
				</PageTreeItemSpinner>
			);
		}

		if (hasChildren || item.data.type === 'folder') {
			return isExpanded ? (
				<button
					onClick={handleOnCollapse}
					data-testid="chevron-down"
					data-no-global-anchor-handler
					css={[beforeIconStyles, isNav4Enabled && beforeIconStylesNav4Styles]}
					aria-expanded={isExpanded}
					aria-labelledby={item.id}
				>
					<ChevronDownIcon color={iconColor} label="" size="small" />
				</button>
			) : (
				<button
					onClick={handleOnExpand}
					data-testid="chevron-right"
					data-no-global-anchor-handler
					css={[beforeIconStyles, isNav4Enabled && beforeIconStylesNav4Styles]}
					aria-expanded={isExpanded}
					aria-labelledby={item.id}
				>
					<ChevronRightIcon color={iconColor} label="" size="small" />
				</button>
			);
		}

		return (
			<NodeIcon label="" color={isNav4Enabled ? token('color.text.subtle') : token('color.icon')} />
		);
	},
);
